import { QRCodeSVG } from "qrcode.react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiClient } from "../api/client.js";

const VENUE_PORTAL_URL = window.serverConfig?.VENUE_PORTAL_URL || "http://localhost:6175";
const CONTENT_APP_URL = window.serverConfig?.CONTENT_APP_URL || "http://localhost:6176";

type State =
  | { phase: "loading" }
  | { phase: "qr"; code: string; expiresAt: string }
  | { phase: "claimed" }
  | { phase: "expired" }
  | { phase: "error"; message: string };

export function Start() {
  const [state, setState] = useState<State>({ phase: "loading" });
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const createCode = useCallback(async () => {
    setState({ phase: "loading" });
    stopPolling();

    try {
      const res = await apiClient["/api/v1/user/setup-codes"]["post"]({
        parameters: {},
        body: null,
      });

      if (res.code === 201) {
        const { code, expires_at } = res.body;
        setState({ phase: "qr", code, expiresAt: expires_at });
      } else {
        setState({ phase: "error", message: "Failed to create setup code" });
      }
    } catch {
      setState({ phase: "error", message: "Network error" });
    }
  }, [stopPolling]);

  // Poll for status changes
  useEffect(() => {
    if (state.phase !== "qr") return;

    const { code } = state;

    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient["/api/v1/user/setup-codes/{code}"]["get"]({
          parameters: { code },
          body: null,
        });

        if (res.code === 200) {
          const body = res.body as {
            code: string;
            status: string;
            device_id?: string;
            auth_token?: string;
          };
          if (body.status === "claimed" && body.auth_token) {
            stopPolling();
            setState({ phase: "claimed" });
            window.location.href = `${CONTENT_APP_URL}?token=${encodeURIComponent(body.auth_token)}`;
          } else if (body.status === "expired") {
            stopPolling();
            setState({ phase: "expired" });
          }
        }
      } catch {
        // Ignore poll errors, keep trying
      }
    }, 3000);

    return () => stopPolling();
  }, [state, stopPolling]);

  useEffect(() => {
    createCode();
  }, [createCode]);

  const qrUrl = state.phase === "qr" ? `${VENUE_PORTAL_URL}/setup-device?code=${state.code}` : "";

  const copyLink = useCallback(() => {
    if (!qrUrl) return;
    navigator.clipboard.writeText(qrUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [qrUrl]);

  if (state.phase === "loading") {
    return (
      <main className="flex flex-col items-center justify-center min-h-screen gap-6 p-6">
        <p className="text-text-secondary">Generating setup code...</p>
      </main>
    );
  }

  if (state.phase === "error") {
    return (
      <main className="flex flex-col items-center justify-center min-h-screen gap-6 p-6">
        <h1 className="text-xl font-bold text-text-primary">Something went wrong</h1>
        <p className="text-text-secondary">{state.message}</p>
        <button
          onClick={createCode}
          className="px-6 py-3 rounded-lg bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
        >
          Try Again
        </button>
      </main>
    );
  }

  if (state.phase === "expired") {
    return (
      <main className="flex flex-col items-center justify-center min-h-screen gap-6 p-6">
        <h1 className="text-xl font-bold text-text-primary">Code Expired</h1>
        <p className="text-text-secondary">The setup code has expired. Generate a new one.</p>
        <button
          onClick={createCode}
          className="px-6 py-3 rounded-lg bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
        >
          Generate New Code
        </button>
      </main>
    );
  }

  if (state.phase === "claimed") {
    return (
      <main className="flex flex-col items-center justify-center min-h-screen gap-6 p-6">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-text-primary">Table Linked!</h1>
        <p className="text-text-secondary">Redirecting to games...</p>
      </main>
    );
  }

  // QR phase
  return (
    <main className="flex flex-col items-center justify-center min-h-screen gap-8 p-6">
      <h1 className="text-2xl font-bold text-text-primary">Add This Table</h1>
      <p className="text-text-secondary text-center max-w-sm">
        Scan this QR code with your phone to link this device to your venue.
      </p>
      <button
        onClick={copyLink}
        className="bg-white p-4 rounded-xl cursor-pointer hover:shadow-lg transition-shadow relative"
        title="Click to copy link"
      >
        <QRCodeSVG value={qrUrl} size={220} />
        {copied && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-xl text-white font-medium">
            Link copied!
          </span>
        )}
      </button>
      <p className="text-3xl font-mono font-bold text-accent tracking-widest">{state.code}</p>
      <p className="text-sm text-text-muted">This code expires in 15 minutes</p>
    </main>
  );
}
